import { useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import { Checkbox } from '@quebon/ui/components/checkbox'
import { Field, FieldContent, FieldDescription, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@quebon/ui/components/field'
import type { ReviewNoteSourceLessonView } from '@/api/lcReviewNote'
import { buildReviewNoteSourceTree, filterSelectableSourceLessonIds, type ReviewNoteSourceNode } from '@/utils/reviewNoteSources'

interface Props {
  lessons: ReviewNoteSourceLessonView[]
  selectedLessonIds: number[]
  onChange: (lessonIds: number[]) => void
  disabled?: boolean
  hideHeading?: boolean
}

function SourceNode({ node, selected, onToggle, disabled }: {
  node: ReviewNoteSourceNode
  selected: Set<number>
  onToggle: (lessonIds: number[], checked: boolean) => void
  disabled: boolean
}) {
  const { t } = useTranslation('coaching')
  const id = useId()
  const [expanded, setExpanded] = useState(() => node.lessonIds.some((lessonId) => selected.has(lessonId)))
  const selectedCount = node.selectableLessonIds.filter((lessonId) => selected.has(lessonId)).length
  const unavailable = node.selectableLessonIds.length === 0
  const checked = !unavailable && selectedCount === node.selectableLessonIds.length
  const indeterminate = selectedCount > 0 && !checked
  const hasChildren = node.children.length > 0
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 items-center gap-2">
        {hasChildren ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t(expanded ? 'reviewNote.collapseRange' : 'reviewNote.expandRange', { title: node.title })}
            aria-expanded={expanded}
            aria-controls={`${id}-children`}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </Button>
        ) : <span className="w-8 shrink-0" aria-hidden="true" />}
        <Field orientation="horizontal" data-disabled={disabled || unavailable} className="min-w-0 flex-1">
          <Checkbox
            id={id}
            checked={checked}
            indeterminate={indeterminate}
            disabled={disabled || unavailable}
            aria-describedby={unavailable && !hasChildren ? `${id}-reason` : undefined}
            onCheckedChange={(value) => onToggle(node.selectableLessonIds, value)}
          />
          <FieldContent className="min-w-0">
            <FieldLabel htmlFor={id} className="min-w-0"><span className="min-w-0 break-words whitespace-normal">{node.title}</span></FieldLabel>
            {unavailable && !hasChildren ? <FieldDescription id={`${id}-reason`}>{t('reviewNote.sourceNotCompleted')}</FieldDescription> : null}
          </FieldContent>
        </Field>
      </div>
      {hasChildren && expanded ? (
        <div id={`${id}-children`} className="ml-4 flex min-w-0 flex-col gap-2 border-l border-border pl-2">
          {node.children.map((child) => (
            <SourceNode key={child.key} node={child} selected={selected} onToggle={onToggle} disabled={disabled} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function ReviewNoteSourceSelector({ lessons, selectedLessonIds, onChange, disabled = false, hideHeading = false }: Props) {
  const { t } = useTranslation('coaching')
  const tree = useMemo(() => buildReviewNoteSourceTree(lessons), [lessons])
  const selected = new Set(filterSelectableSourceLessonIds(selectedLessonIds, lessons))
  const onToggle = (lessonIds: number[], checked: boolean) => {
    const next = new Set(selected)
    lessonIds.forEach((id) => { if (checked) next.add(id); else next.delete(id) })
    onChange(filterSelectableSourceLessonIds([...next], lessons))
  }
  return (
    <FieldSet disabled={disabled} aria-label={hideHeading ? t('reviewNote.rangeLabel') : undefined}>
      {!hideHeading ? <FieldLegend variant="label">{t('reviewNote.rangeLabel')}</FieldLegend> : null}
      <FieldDescription>{t('reviewNote.rangeGuide')}</FieldDescription>
      <FieldGroup className="max-h-80 overflow-y-auto rounded-md border border-border p-3">
        {tree.map((node) => <SourceNode key={node.key} node={node} selected={selected} onToggle={onToggle} disabled={disabled} />)}
      </FieldGroup>
    </FieldSet>
  )
}
